import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { GraduationCap, Eye, EyeOff, Loader2, Building2, Shield } from 'lucide-react';

import { BACKEND_URL } from '@/config';
const API = `${BACKEND_URL}/api`;

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: '',
    phone: '',
  });

  useEffect(() => {
    if (token) fetchInviteInfo();
    else setError('No invitation token provided');
  }, [token]);

  const fetchInviteInfo = async () => {
    try {
      const res = await axios.get(`${API}/auth/invite/${token}`);
      setInviteInfo(res.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirm_password) {
      setError('Passwords do not match');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('First and last name are required');
      return;
    }

    setSubmitting(true);
    try {
      const params = new URLSearchParams({
        token,
        first_name: form.first_name,
        last_name: form.last_name,
        password: form.password,
        ...(form.phone && { phone: form.phone }),
      });
      const res = await axios.post(`${API}/auth/accept-invite?${params.toString()}`, {}, { withCredentials: true });

      // Tokens are now in httpOnly cookies — just redirect
      // Redirect to dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] via-[#F8FAFC] to-[#f8fafc]">
        <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
      </div>
    );
  }

  if (!inviteInfo && error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] via-[#F8FAFC] to-[#f8fafc] px-4">
        <Card className="w-full max-w-md border-[#f8fafc] shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-[#F4A261]/15 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-[#F4A261]" />
            </div>
            <h2 className="text-xl font-bold text-[#2a3e5d] mb-2">Invalid Invitation</h2>
            <p className="text-[#1e293b]/60 mb-6">{error}</p>
            <Button asChild className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white">
              <Link to="/">Go to Homepage</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] via-[#F8FAFC] to-[#f8fafc] px-4 py-12" data-testid="accept-invite-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8">
          <div className="w-11 h-11 bg-[#20acf6] rounded-xl flex items-center justify-center">
            <GraduationCap className="w-6 h-6 text-white" />
          </div>
          <span className="font-semibold text-xl text-[#1e293b]">Common Impact</span>
        </Link>

        <Card className="border-[#f8fafc] shadow-lg bg-white">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-[#1e293b]">Join Your Team</CardTitle>
            <CardDescription className="text-[#1e293b]/60">
              Complete your account to join {inviteInfo?.organisation_name}
            </CardDescription>
            <div className="flex items-center justify-center gap-2 mt-2">
              <Badge className="bg-[#f8fafc] text-[#02d1d4]">
                <Building2 className="h-3 w-3 mr-1" />
                {inviteInfo?.organisation_name}
              </Badge>
              <Badge className="bg-[#f8fafc] text-[#20acf6] capitalize">
                {inviteInfo?.team_role}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert className="bg-[#F4A261]/10 border-[#F4A261]/30 text-[#1e293b]">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label className="text-[#1e293b]">Email</Label>
                <Input value={inviteInfo?.email || ''} disabled className="bg-gray-50" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#1e293b]">First name</Label>
                  <Input
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    required
                    className="border-[#f8fafc] focus:border-[#20acf6] focus:ring-[#20acf6]"
                    data-testid="invite-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1e293b]">Last name</Label>
                  <Input
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    required
                    className="border-[#f8fafc] focus:border-[#20acf6] focus:ring-[#20acf6]"
                    data-testid="invite-last-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[#1e293b]">Phone (optional)</Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+44 123 456 7890"
                  className="border-[#f8fafc] focus:border-[#20acf6] focus:ring-[#20acf6]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#1e293b]">Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Min. 8 characters"
                    required
                    className="border-[#f8fafc] focus:border-[#20acf6] focus:ring-[#20acf6]"
                    data-testid="invite-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1e293b]/40"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[#1e293b]">Confirm password</Label>
                <Input
                  type="password"
                  value={form.confirm_password}
                  onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                  required
                  className="border-[#f8fafc] focus:border-[#20acf6] focus:ring-[#20acf6]"
                  data-testid="invite-confirm-password"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#20acf6] hover:bg-[#3bb8f7] text-white"
                disabled={submitting}
                data-testid="accept-invite-submit"
              >
                {submitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating account...</>
                ) : (
                  'Create Account & Join'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
